import router from '@app/router'
import HeatMetricsModel from '@app/models/heat-metrics'
import UserModel from '@app/models/users'
import { LIST_HEAT_METRICS_MAP_WITH_SIDE_BAR_KEY, USER_MANAGEMENT_SOLUTION_TYPE } from '@shared/constants'
import { isMetric } from '@app/helpers/unit-system.js'

// Helper for unit-aware descriptions
const perUnit = () => isMetric() ? 'Kilometers' : 'Miles'

function safetyPermissionCheckFn() {
    return UserModel.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.SAFETY)
}

class HeatMetricState {

    // =========================================================================
    // CONFIG 1: SELECTED_METRICS - All Abacus/Cerberus metrics
    // Maps selected_metric key to its properties including hmm mapping
    // =========================================================================

    static SELECTED_METRICS = {
        // =====================================================================
        // MOBILITY MODE - Activity Section
        // =====================================================================
        'volume': {
            title: 'Volume',
            description: () => 'Volume per Hour',
            hmm: 'volume',
            api_field: 'annual_average_daily_traffic',
            format: 'count',
            default_thresholds: {
                above_threshold: 500,
                above_threshold_2: 1000,
                above_threshold_3: 2500,
                above_threshold_4: 5000,
            }
        },
        'vmt': {
            title: 'VMT',
            description: () => 'Volume per Hour',
            hmm: 'volume',
            api_field: 'vehicle_miles_traveled',
            format: 'count',
            default_thresholds: {
                above_threshold: 500,
                above_threshold_2: 1000,
                above_threshold_3: 2500,
                above_threshold_4: 5000,
            }
        },

        // =====================================================================
        // MOBILITY MODE - Mobility Section
        // =====================================================================
        'travel-time': {
            title: 'Travel Time',
            description: () => 'Speed Ratio',
            hmm: 'mobility',
            api_field: 'travel_time_mean_seconds',
            format: 'rate',
            default_thresholds: {
                above_threshold: 1,
                above_threshold_2: 0.8,
                above_threshold_3: 0.6,
                above_threshold_4: 0.4,
            }
        },
        'total-delay': {
            title: 'Total Delay',
            description: () => 'Speed Ratio',
            hmm: 'mobility',
            api_field: 'delay_per_vehicle_mean_seconds',
            format: 'rate',
            default_thresholds: {
                above_threshold: 1,
                above_threshold_2: 0.8,
                above_threshold_3: 0.6,
                above_threshold_4: 0.4,
            }
        },
        'average-traversal-speed': {
            title: 'Average Route Speed',
            description: () => 'Route Speed Ratio',
            hmm: 'mobility',
            api_field: 'traversal_speed_miles_h_mean',
            format: 'rate',
            default_thresholds: {
                above_threshold: 1,
                above_threshold_2: 0.8,
                above_threshold_3: 0.6,
                above_threshold_4: 0.4,
            }
        },
        'average-speed': {
            title: 'Average Speed',
            description: () => 'Speed Ratio',
            hmm: 'mobility',
            api_field: 'speed_miles_h_mean',
            format: 'rate',
            default_thresholds: {
                above_threshold: 1,
                above_threshold_2: 0.8,
                above_threshold_3: 0.6,
                above_threshold_4: 0.4,
            }
        },
        'travel-time-index': {
            title: 'Travel Time Index',
            description: () => 'Speed Ratio',
            hmm: 'mobility',
            api_field: 'travel_time_index',
            format: 'rate',
            default_thresholds: {
                above_threshold: 1,
                above_threshold_2: 0.8,
                above_threshold_3: 0.6,
                above_threshold_4: 0.4,
            }
        },
        'buffer-index': {
            title: 'Buffer Index',
            description: () => 'Speed Ratio',
            hmm: 'mobility',
            api_field: 'buffer_index',
            format: 'rate',
            default_thresholds: {
                above_threshold: 1,
                above_threshold_2: 0.8,
                above_threshold_3: 0.6,
                above_threshold_4: 0.4,
            }
        },
        'planning-index': {
            title: 'Planning Index',
            description: () => 'Speed Ratio',
            hmm: 'mobility',
            api_field: 'planning_index',
            format: 'rate',
            default_thresholds: {
                above_threshold: 1,
                above_threshold_2: 0.8,
                above_threshold_3: 0.6,
                above_threshold_4: 0.4,
            }
        },

        // =====================================================================
        // SAFETY MODE - Activity Section
        // =====================================================================
        'total-vmt': {
            title: 'Total VMT',
            description: () => 'Total Vehicle Miles Traveled',
            hmm: null, // No map coloring for activity metrics
            api_field: 'vehicle_miles_traveled',
            format: 'count',
            should_hide: true
        },
        'pedestrian-activity': {
            title: 'Pedestrian Activity',
            description: () => 'Pedestrian Activity Level',
            hmm: 'pedestrian',
            hcs: 'g2r',
            api_field: 'max_pedestrian_count',
            threshold_key: 'max_pedestrian_count',
            format: 'count',
        },

        // =====================================================================
        // SAFETY MODE - Behavior Section
        // =====================================================================
        'high-speed-events': {
            title: 'High Speed Events',
            description: () => `Speeding Violation Events per 100 Million ${perUnit()}`,
            hmm: 'speeding',
            hcs: 'g2r',
            api_field: 'michelin_speeding_rate_events_per_h_m_miles',
            threshold_key: 'michelin_speeding_rate_events',
            format: 'rate',
            permission_check_fn: safetyPermissionCheckFn
        },
        'hard-braking-events': {
            title: 'Hard Braking Events',
            description: () => `Hard Braking Events per 100 Million ${perUnit()}`,
            hmm: 'hard_braking',
            hcs: 'g2r',
            api_field: 'hard_braking_rate_events_per_h_m_miles',
            threshold_key: 'hard_braking_rate_events',
            format: 'rate',
            permission_check_fn: safetyPermissionCheckFn
        },
        'aggressive-acceleration': {
            title: 'Aggressive Acceleration',
            description: () => `Aggressive Acceleration per 100 Million ${perUnit()}`,
            hmm: 'aggressive_acceleration',
            hcs: 'g2r',
            api_field: 'aggressive_acceleration_rate_events_per_h_m_miles',
            threshold_key: 'aggressive_acceleration_rate_events',
            format: 'rate',
            permission_check_fn: safetyPermissionCheckFn
        },
        'distracted-driving': {
            title: 'Distracted Driving',
            description: () => `Distracted Driving per 100 Million ${perUnit()}`,
            hmm: 'distracted_driver',
            hcs: 'g2r',
            api_field: 'distracted_driver_rate_events_per_h_m_miles',
            threshold_key: 'distracted_driver_rate_events',
            format: 'rate',
            permission_check_fn: safetyPermissionCheckFn
        },
        'suspected-collision': {
            title: 'Suspected Collision',
            description: () => `Suspected Collisions per 100 Million ${perUnit()}`,
            hmm: 'suspected_collisions',
            hcs: 'g2r',
            api_field: 'michelin_crash_rate_events_per_h_m_miles',
            threshold_key: 'michelin_crash_rate_events',
            format: 'rate',
            permission_check_fn: safetyPermissionCheckFn
        },

        // =====================================================================
        // SAFETY MODE - Outcomes (Crashes) Section
        // =====================================================================
        'crashes': {
            title: 'Crashes',
            description: () => 'Number of Crashes',
            hmm: 'crashes',
            hcs: 'g2r',
            api_field: 'crash_sample_size',
            threshold_key: 'number_of_crashes',
            format: 'count',
            permission_check_fn: safetyPermissionCheckFn
        },
        'crash-rate': {
            title: 'Crash Rate',
            description: () => `Crashes per 100 Million ${perUnit()}`,
            hmm: 'crashes',
            hcs: 'g2r',
            api_field: 'crash_rate_events_per_h_m_miles',
            threshold_key: 'crash_rate_events',
            format: 'rate',
            permission_check_fn: safetyPermissionCheckFn
        },
        'ksi-crashes': {
            title: 'KSI Crashes',
            description: () => 'Killed or Seriously Injured Crashes',
            hmm: 'ksi_crashes',
            hcs: 'g2r',
            api_field: 'severe_crash_sample_size',
            threshold_key: 'number_of_crashes',
            format: 'count',
            permission_check_fn: safetyPermissionCheckFn
        },
        'ksi-crash-rate': {
            title: 'KSI Crash Rate',
            description: () => `KSI Crashes per 100 Million ${perUnit()}`,
            hmm: 'ksi_crashes',
            hcs: 'g2r',
            api_field: 'severe_crash_rate_events_per_h_m_miles',
            threshold_key: 'crash_rate_events',
            format: 'rate',
            permission_check_fn: safetyPermissionCheckFn
        },
        'fatalities': {
            title: 'Fatal Crashes',
            description: () => 'Number of Fatal Crashes',
            hmm: 'fatalities',
            hcs: 'g2r',
            api_field: 'fatality_sample_size',
            threshold_key: 'number_of_fatalities',
            format: 'count',
            permission_check_fn: safetyPermissionCheckFn
        },
        'fatality-rate': {
            title: 'Fatal Crashes Rate',
            description: () => `Fatal Crashes per 100 Million ${perUnit()}`,
            hmm: 'fatalities',
            hcs: 'g2r',
            api_field: 'fatality_rate_events_per_h_m_miles',
            threshold_key: 'fatality_rate_events',
            format: 'rate',
            permission_check_fn: safetyPermissionCheckFn
        },

        // =====================================================================
        // SIGNAL OPS MODE - Metrics
        // =====================================================================
        'health-score': {
            title: 'Health Score',
            description: () => 'Signal Health Score',
            hmm: 'health-score',
            api_field: 'fl_health_score',
            format: 'rate',
        },
        'control-delay': {
            title: 'Control Delay / LOS',
            description: () => 'Average Control Delay (seconds)',
            hmm: 'control-delay',
            api_field: 'fl_control_delay_per_vehicle_mean_seconds',
            format: 'rate',
        },
        'arrivals-on-green': {
            title: 'Arrivals on Green',
            description: () => 'Arrivals on Green Percentage',
            hmm: 'arrivals-on-green',
            api_field: 'fl_arrivals_on_green_yellow_ratio',
            format: 'rate',
        },
        'split-failures': {
            title: 'Split Failures',
            description: () => 'Split Failure Count',
            hmm: 'split-failures',
            api_field: 'fl_split_failure_count',
            format: 'count',
        },
        'signal-volume': {
            title: 'Signal Volume',
            description: () => 'Signal Volume Count',
            hmm: 'signal-volume',
            api_field: 'fl_volume_count_per_hour',
            format: 'count',
        },
        'spillback': {
            title: 'Spillback',
            description: () => 'Spillback Ratio',
            hmm: 'spillback',
            api_field: 'spillback_ratio',
            format: 'rate',
            // ratio in [0,1], higher = worse
            default_thresholds: {
                above_threshold: 0.1,
                above_threshold_2: 0.25,
                above_threshold_3: 0.5,
                above_threshold_4: 0.75,
            }
        },
    }

    // =========================================================================
    // CONFIG 2: HMM_OPTIONS - Map coloring options per mode
    // =========================================================================

    static HMM_OPTIONS = {
        mobility: {
            options: [
                'volume', 'mobility',
                // ⬇️ merge of safety metrics into mobility ⬇️
                'crashes', 'severe_crashes', 'fatalities', 'hard_braking', 'speeding', 'pedestrian', 'aggressive_acceleration', 'distracted_driver', 'suspected_collisions'
            ],
            default: 'mobility',
            // Maps hmm to display title and default selected_metric
            titles: {
                'volume': 'Volume',
                'mobility': 'Mobility',
                // ⬇️ merge of safety metrics into mobility ⬇️
                'crashes': 'Crashes',
                'severe_crashes': 'Severe Crashes',
                'fatalities': 'Fatal Crashes',
                'hard_braking': 'Hard Braking Events',
                'speeding': 'High Speed Events',
                'pedestrian': 'Pedestrian Activity',
                'aggressive_acceleration': 'Aggressive Acceleration',
                'distracted_driver': 'Distracted Driving',
                'suspected_collisions': 'Suspected Collision',
            },
            defaultSelectedMetric: {
                'volume': 'volume',
                'mobility': 'travel-time',
                // ⬇️ merge of safety metrics into mobility ⬇️
                'crashes': 'crashes',
                'severe_crashes': 'ksi-crashes',
                'fatalities': 'fatalities',
                'hard_braking': 'hard-braking-events',
                'speeding': 'high-speed-events',
                'pedestrian': 'pedestrian-activity',
                'aggressive_acceleration': 'aggressive-acceleration',
                'distracted_driver': 'distracted-driving',
                'suspected_collisions': 'suspected-collision',
            },
        },
        sigops: {
            options: ['control-delay', 'health-score'],
            default: 'control-delay',
            titles: {
                'health-score': 'Health Score',
                'control-delay': 'Control Delay / LOS',
                'spillback': 'Spillback',
            },
            defaultSelectedMetric: {
                'health-score': 'health-score',
                'control-delay': 'control-delay',
                'spillback': 'spillback',
            },
        },
        safety: {
            options: ['crashes', 'severe_crashes', 'fatalities', 'hard_braking', 'speeding', 'pedestrian', 'aggressive_acceleration', 'distracted_driver', 'suspected_collisions'],
            default: 'crashes',
            titles: {
                'crashes': 'Crashes',
                'severe_crashes': 'Severe Crashes',
                'fatalities': 'Fatal Crashes',
                'hard_braking': 'Hard Braking Events',
                'speeding': 'High Speed Events',
                'pedestrian': 'Pedestrian Activity',
                'aggressive_acceleration': 'Aggressive Acceleration',
                'distracted_driver': 'Distracted Driving',
                'suspected_collisions': 'Suspected Collision',
            },
            defaultSelectedMetric: {
                'crashes': 'crashes',
                'severe_crashes': 'ksi-crashes',
                'fatalities': 'fatalities',
                'hard_braking': 'hard-braking-events',
                'speeding': 'high-speed-events',
                'pedestrian': 'pedestrian-activity',
                'aggressive_acceleration': 'aggressive-acceleration',
                'distracted_driver': 'distracted-driving',
                'suspected_collisions': 'suspected-collision',
            },
        },
        rt: {
            options: ['mobility'],
            default: 'mobility',
            titles: {
                'mobility': 'Mobility',
            },
            defaultSelectedMetric: {
                'mobility': 'travel-time',
            },
        },
    }

    // =========================================================================
    // CONFIG 3: ABACUS_SECTIONS - UI structure for Abacus/Cerberus
    // =========================================================================

    // NOTE: metrics listed here must remain synced with the hard-coded metrics in the agent tool shared schema
    // (set_roadways_selected_metric_valibot_schema in shared-schemas/agent-chat/agent-tool-inputs.ts)

    static ABACUS_SECTIONS = {
        mobility: [
            {
                key: 'activity',
                title: 'Activity',
                metrics: [
                    'volume',
                    'vmt',
                    // ⬇️ merge of safety metrics into mobility ⬇️
                    'total-vmt',
                    'pedestrian-activity'
                ]
            },
            { key: 'mobility', title: 'Mobility', metrics: ['travel-time', 'total-delay', 'average-speed', 'average-traversal-speed', 'travel-time-index', 'buffer-index', 'planning-index'] },

            // ⬇️ merge of safety metrics into mobility ⬇️
            { key: 'behavior', title: 'Behavior', metrics: ['high-speed-events', 'hard-braking-events', 'aggressive-acceleration', 'distracted-driving', 'suspected-collision'] },
            { key: 'outcomes', title: 'Outcomes', metrics: ['crashes', 'crash-rate', 'ksi-crashes', 'ksi-crash-rate', 'fatalities', 'fatality-rate'] },
        ],
        sigops: [
            { key: 'sigops', title: 'Signal Ops', metrics: ['control-delay', 'health-score'] },
        ],
        safety: [
            { key: 'activity', title: 'Activity', metrics: ['total-vmt', 'pedestrian-activity'] },
            { key: 'behavior', title: 'Behavior', metrics: ['high-speed-events', 'hard-braking-events', 'aggressive-acceleration', 'distracted-driving', 'suspected-collision'] },
            { key: 'outcomes', title: 'Outcomes', metrics: ['crashes', 'crash-rate', 'ksi-crashes', 'ksi-crash-rate', 'fatalities', 'fatality-rate'] },
        ],
        rt: [
            { key: 'mobility', title: 'Mobility', metrics: ['travel-time', 'average-speed'] },
        ],
    }

    // =========================================================================
    // ENTITY (TAB) CONFIGURATION - For cerberus sidebar tabs
    // =========================================================================

    static ENTITIES = {
        'edge-groups': {
            title: 'Roadways',
            grouping_criteria: 'speed_limit_and_road_name',
            row_id_key: 'edge_group_id',
            location_key: 'road_name',
            route: 'roadway',
            default_sidebar: 'right',
            api_version: 'v2',
            modes: ['safety', 'mobility'],
        },
        'routes': {
            title: 'Routes',
            grouping_criteria: 'mainline', // Note: 'mainline' filters for 'mainline' and 'user_defined_route'
            row_id_key: 'edge_group_id',
            location_key: 'road_name',
            route: 'roadway',
            default_sidebar: 'right',
            api_version: 'v2',
            modes: ['safety', 'mobility'],
        },
        networks: {
            title: 'Networks',
            row_id_key: 'network_id',
            location_key: 'network_name',
            route: { sigops: 'sigops_network' },
            default_sidebar: 'right',
            requiresSignalOps: true,
            api_version: 'v1',
            modes: ['sigops'],
        },
        signals: {
            title: 'Signals',
            row_id_key: 'signal_id',
            location_key: 'signal_name',
            route: { sigops: 'sigops_signal' },
            default_sidebar: 'right',
            requiresSignalOps: true,
            api_version: 'v1',
            modes: ['sigops'],
        },
        phases: {
            title: 'Phases',
            row_id_key: 'phase_id',
            location_key: 'signal_phase_name',
            route: { sigops: 'sigops_phase' },
            default_sidebar: 'right',
            requiresSignalOps: true,
            api_version: 'v1',
            modes: ['sigops'],
        },
    }

    // =========================================================================
    // SIDEPANEL SUPPORTED METRICS - Which metrics support v1 side-panel API
    // =========================================================================

    static SIDEPANEL_SUPPORTED_METRICS = {
        'networks': [
            'speeding', 'hard_braking', 'pedestrian', 'aggressive_acceleration',
            'distracted_driver', 'suspected_collisions',
            'fatalities', 'number_of_fatalities', 'crashes', 'number_of_crashes'
        ],
        'signals': [
            'speeding', 'hard_braking', 'pedestrian', 'aggressive_acceleration',
            'distracted_driver', 'suspected_collisions',
            'fatalities', 'number_of_fatalities', 'crashes', 'number_of_crashes'
        ],
        // Edge-groups use v2 API (handled separately) - all metrics supported
        'edge-groups': null,  // null = no restriction (uses v2 API)
        // Routes use v2 API (handled separately) - all metrics supported
        'routes': null,  // null = no restriction (uses v2 API)
    }

    // =========================================================================
    // INITIALIZATION
    // =========================================================================

    constructor() {
        this._unsubscribe = null
        this._initialized = false
        this._syncing = false
    }

    init() {
        if (this._initialized) return

        this._unsubscribe = router.onNavigation((route) => {
            this._syncOnNavigation(route)
        })

        this._initialized = true
        console.log('[HeatMetricStateModel] Initialized with navigation sync')
    }

    // =========================================================================
    // GETTERS - Simple lookups, no validation
    // =========================================================================

    getMode() {
        const route = router.currentRoute()
        if (route?.query?.mode) return route.query.mode
        if (route?.name?.startsWith('sigops') || route?.key?.includes('sigops')) return 'sigops'
        if (route?.name?.startsWith('mobility') || route?.key?.includes('mobility')) return 'mobility'
        if (route?.name?.startsWith('safety') || route?.key?.includes('safety')) return 'safety'
        return 'safety'
    }

    getHmm() {
        const route = router.currentRoute()
        const mode = this.getMode()
        const modeConfig = HeatMetricState.HMM_OPTIONS[mode]
        const hmm = route?.query?.hmm
        const selected_metric_hmm = this.selectedMetricToHmm(route?.query?.selected_metric)
        if (selected_metric_hmm && modeConfig?.options?.includes(selected_metric_hmm)) return selected_metric_hmm
        if (hmm && modeConfig?.options?.includes(hmm)) return hmm
        return modeConfig?.default || 'crashes'
    }

    getSelectedMetric() {
        const route = router.currentRoute()
        const mode = this.getMode()
        const modeConfig = HeatMetricState.HMM_OPTIONS[mode]

        if (route?.query?.selected_metric) {
            const selected_metric_hmm = this.selectedMetricToHmm(route.query.selected_metric)
            if (!modeConfig?.options || modeConfig.options.includes(selected_metric_hmm)) {
                return route.query.selected_metric
            }
        }
        // Derive from hmm using defaultSelectedMetric
        const hmm = this.getHmm()
        return modeConfig?.defaultSelectedMetric?.[hmm] || 'crashes'
    }

    // =========================================================================
    // METRIC INFO GETTERS
    // =========================================================================

    getMetricConfig(selected_metric) {
        return HeatMetricState.SELECTED_METRICS[selected_metric] || null
    }

    getMetricConfigByHmm(hmm_value) {
        return Object.values(HeatMetricState.SELECTED_METRICS).find(metric => metric.hmm === hmm_value) ?? null
    }

    getMetricTitle(selected_metric) {
        return HeatMetricState.SELECTED_METRICS[selected_metric]?.title || selected_metric
    }

    getMetricDescription(selected_metric) {
        const descFn = HeatMetricState.SELECTED_METRICS[selected_metric]?.description
        return descFn ? descFn() : selected_metric
    }

    getMetricShouldHide(selected_metric) {
        const should_hide = HeatMetricState.SELECTED_METRICS[selected_metric]?.should_hide
        return should_hide || false
    }

    getMetricPermissionCheckFn(selected_metric) {
        return HeatMetricState.SELECTED_METRICS[selected_metric]?.permission_check_fn ?? (() => true)
    }

    isRateMetric(selected_metric) {
        return HeatMetricState.SELECTED_METRICS[selected_metric]?.format === 'rate'
    }

    getApiFieldForSelectedMetric(selected_metric) {
        return HeatMetricState.SELECTED_METRICS[selected_metric]?.api_field || null
    }

    getThresholdKeyForSelectedMetric(selected_metric) {
        return HeatMetricState.SELECTED_METRICS[selected_metric]?.threshold_key || null
    }


    getDefaultThresholdsForSelectedMetric(selected_metric) {
        return HeatMetricState.SELECTED_METRICS[selected_metric]?.default_thresholds || null
    }

    /**
     * Check if a metric is supported for side-panel API calls for a given entity type
     */
    isSidepanelSupported(entity_type, selected_metric) {
        const supported = HeatMetricState.SIDEPANEL_SUPPORTED_METRICS[entity_type]
        // null means no restrictions (e.g., edge-groups use v2 API)
        if (supported === null) return true
        // undefined means entity has no side-panel
        if (supported === undefined) return false
        return supported.includes(selected_metric)
    }

    selectedMetricToHmm(selected_metric) {
        return HeatMetricState.SELECTED_METRICS[selected_metric]?.hmm || null
    }

    /**
     * Get the section (tab) key for a given metric
     */
    getSectionKeyForMetric(selected_metric, mode = null) {
        const currentMode = mode || this.getMode()
        const sections = HeatMetricState.ABACUS_SECTIONS[currentMode] || []

        for (const section of sections) {
            if (section.metrics.includes(selected_metric)) {
                return section.key
            }
        }
        return null
    }

    hmmToSelectedMetric(hmm, mode = null) {
        const currentMode = mode || this.getMode()
        return HeatMetricState.HMM_OPTIONS[currentMode]?.defaultSelectedMetric?.[hmm] || null
    }

    // =========================================================================
    // OPTIONS GETTERS - For populating UI dropdowns/pills
    // =========================================================================

    /**
     * Get Abacus/Cerberus metrics as flat array
     * Optionally filtered by customer's enabled metrics (visual only)
     */
    getAbacusMetricOptions(mode, filterByCustomer = true) {
        const sections = HeatMetricState.ABACUS_SECTIONS[mode] || []
        const allMetrics = sections.flatMap(section =>
            section.metrics.map(key => ({
                key,
                title: this.getMetricTitle(key),
                description: this.getMetricDescription(key),
                should_hide: this.getMetricShouldHide(key),
                permission_check_fn: this.getMetricPermissionCheckFn(key)
            })).filter(metric => metric.permission_check_fn())
        )

        if (!filterByCustomer) return allMetrics

        // Filter by customer's enabled metrics (visual only, not validation)
        const enabledApiMetrics = HeatMetricsModel.metric_keys || []

        if (enabledApiMetrics.length === 0) return allMetrics

        return allMetrics.filter(m => {
            const hmm = this.selectedMetricToHmm(m.key)
            const apiKey = LIST_HEAT_METRICS_MAP_WITH_SIDE_BAR_KEY[hmm]
            // Keep metric if no API mapping OR if it's in enabled list
            return !apiKey || enabledApiMetrics.includes(apiKey)
        })
    }

    /**
     * Get HMM options for map settings dropdown
     */
    getHmmOptions(mode, filterByCustomer = true) {
        const config = HeatMetricState.HMM_OPTIONS[mode]
        if (!config) return []

        const enabledApiMetrics = HeatMetricsModel.metric_keys || []

        return config.options
            .filter(hmm => {
                if (!filterByCustomer || enabledApiMetrics.length === 0) return true
                const apiKey = LIST_HEAT_METRICS_MAP_WITH_SIDE_BAR_KEY[hmm]
                return !apiKey || enabledApiMetrics.includes(apiKey)
            })
            .map(hmm => ({
                key: hmm,
                title: config.titles?.[hmm] || hmm,
            }))
    }

    /**
     * Get default URL params for mode switching (MapSelector)
     * Returns { hmm, selected_metric, selected_tab } for the given mode
     */
    getModeDefaults(mode) {
        const modeConfig = HeatMetricState.HMM_OPTIONS[mode]
        const abacusSections = HeatMetricState.ABACUS_SECTIONS[mode]

        if (!modeConfig) return null

        const defaultHmm = modeConfig.default
        const defaultSelectedMetric = modeConfig.defaultSelectedMetric?.[defaultHmm]

        // Find the section that contains the default metric
        let defaultSelectedTab = abacusSections?.[0]?.key
        for (const section of abacusSections || []) {
            if (section.metrics.includes(defaultSelectedMetric)) {
                defaultSelectedTab = section.key
                break
            }
        }

        return {
            hmm: defaultHmm,
            selected_metric: defaultSelectedMetric,
            selected_tab: defaultSelectedTab,
        }
    }

    // =========================================================================
    // ENTITY (TAB) GETTERS
    // =========================================================================

    getTab() {
        const route = router.currentRoute()
        const tab = route?.query?.sidebar_c_tab
        const mode = this.getMode()
        const entity = HeatMetricState.ENTITIES[tab]
        // Fall back to first mode-valid entity if tab is missing, not in ENTITIES, or not valid for current mode
        if (!tab || !entity || (entity.modes && !entity.modes.includes(mode))) {
            const fallback = Object.entries(HeatMetricState.ENTITIES).find(([, config]) => !config.modes || config.modes.includes(mode))
            return fallback ? fallback[0] : Object.keys(HeatMetricState.ENTITIES)[0]
        }
        return tab
    }

    getEntityConfig(tab) {
        return HeatMetricState.ENTITIES[tab] || null
    }

    getTabOptions() {
        // Check if user has signal-operations permission
        const hasSignalOpsPermission = UserModel.checkPermission(
            USER_MANAGEMENT_SOLUTION_TYPE.SIGNAL
        )
        const mode = this.getMode()

        return Object.entries(HeatMetricState.ENTITIES)
            .filter(([key, config]) => {
                // Filter by mode if entity specifies allowed modes
                if (config.modes && !config.modes.includes(mode)) {
                    return false
                }
                // Filter out tabs that require signal-operations if user doesn't have permission
                if (config.requiresSignalOps && !hasSignalOpsPermission) {
                    return false
                }
                return true
            })
            .map(([key, config]) => ({
                key,
                title: config.title,
            }))
    }

    createRowLink({ tab, id, hmm, network_id, region_id, signal_id }) {
        const entityConfig = this.getEntityConfig(tab)
        if (!entityConfig) return null

        const currentRoute = router.currentRoute()
        const currentParams = currentRoute?.params || {}
        const mode = this.getMode()

        // Get selected_metric for this hmm
        const selected_metric = HeatMetricState.HMM_OPTIONS[mode]?.defaultSelectedMetric?.[hmm]

        const query = {
            active_sidebar: entityConfig.default_sidebar,
            hmm,
            mode,
			// Unlike Fullscreen Cerberus, Sidebar Cerberus should link to the entity at its latest_metrics_date
			dst: undefined,
			dlt: undefined,
        }

        if (selected_metric) {
            query.selected_metric = selected_metric
        }

        if (tab === 'edge-groups' || tab === 'routes') {
            query.sidebar_view = 'abacus'
        }

        // Resolve route name: string or mode-keyed object
        const routeConfig = entityConfig.route
        const routeName = typeof routeConfig === 'string' ? routeConfig : (routeConfig[mode] || routeConfig.safety)
        if (!routeName) return null

        const params = {
            customer_id: currentParams.customer_id,
            region_id: region_id || currentParams.region_id,
            network_id: network_id ? network_id : currentParams.network_id,
        }

        if (tab === 'edge-groups' || tab === 'routes') {
            params.roadway_id = id
        } else if (tab === 'networks') {
            params.network_id = id
        } else if (tab === 'signals') {
            params.signal_id = id
        } else if (tab === 'phases') {
            params.signal_id = signal_id
            params.phase_id = id
        }

        return router.routes[routeName]?.useNavigation({
            params,
            query,
            options: { preserve: true },
        })
    }

    createTabLink(tabKey) {
        const route = router.currentRoute()
        return route?.useNavigation({
            query: { sidebar_c_tab: tabKey },
            options: { preserve: true },
        })
    }

    // =========================================================================
    // SETTERS - Navigate with derived values
    // =========================================================================

    setSelectedMetric(selected_metric, options = {}) {
        const config = HeatMetricState.SELECTED_METRICS[selected_metric]
        if (!config) {
            console.warn(`[HeatMetricState] Unknown selected_metric: "${selected_metric}"`)
            return
        }

        const route = router.currentRoute()
        const hmm = config.hmm
        const selected_tab = this.getSectionKeyForMetric(selected_metric)

        const query = { selected_metric, hmm }
        if (selected_tab) {
            query.selected_tab = selected_tab
        }

        this._syncing = true
        route.navigate({
            query,
            options: { preserve: true, ...options },
        })
        this._syncing = false
    }

    // =========================================================================
    // SYNC LOGIC - Log warnings for mismatched values, no auto-navigation
    // =========================================================================

    _syncOnNavigation(route) {
        if (this._syncing) return
        if (!route) return

        const { hmm, selected_metric, mode = 'safety' } = route.query || {}

        if (!hmm && !selected_metric) return

        const update_selected_tab = route.query.selected_tab !== 'insights'

        if (selected_metric && !hmm) {
            const derivedHmm = this.selectedMetricToHmm(selected_metric)
            if (derivedHmm) {
                this._syncing = true
                route.navigate({
                    query: { hmm: derivedHmm },
                    options: { preserve: true, replace: true },
                })
                this._syncing = false
            } else {
                console.warn(`[HeatMetricState] Unknown selected_metric "${selected_metric}" - no hmm mapping found`)
            }
        } else if (hmm && !selected_metric) {
            const derivedSelected = HeatMetricState.HMM_OPTIONS[mode]?.defaultSelectedMetric?.[hmm]
            if (derivedSelected) {
                const selected_tab = update_selected_tab ? this.getSectionKeyForMetric(derivedSelected, mode) : undefined

                this._syncing = true
                route.navigate({
                    query: {
                        selected_metric: derivedSelected,
                        ...(selected_tab && { selected_tab }),
                    },
                    options: { preserve: true, replace: true },
                })
                this._syncing = false
            } else {
                console.warn(`[HeatMetricState] Unknown hmm "${hmm}" for mode "${mode}" - no selected_metric mapping found`)
            }
        } else if (hmm && selected_metric) {
            const selectedMetricHmm = this.selectedMetricToHmm(selected_metric)
            const derivedSelected = HeatMetricState.HMM_OPTIONS[mode]?.defaultSelectedMetric?.[hmm]

            if (selectedMetricHmm && derivedSelected && selectedMetricHmm !== hmm) {
                const selected_tab = update_selected_tab ? this.getSectionKeyForMetric(derivedSelected, mode) : undefined

                this._syncing = true
                route.navigate({
                    query: {
                        selected_metric: derivedSelected,
                        ...(selected_tab && { selected_tab }),
                    },
                    options: { preserve: true, replace: true },
                })
                this._syncing = false
            }
        }
    }
}

export default new HeatMetricState()
